/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import WebStorage from '../storage/WebStorage';
import SearchService from '../services/SearchService';
import FloodgateService from '../services/FloodgateService';
import BotService from '../services/BotService';
import { SearchResults } from '../services/search-manager';
import * as ActionTypes from '../actionTypes';
import SearchUtils from '../utils/SearchUtils';
import YourWorkUtils from '../containers/SearchResultsPage/YourWork/Utils/YourWorkUtils';
import GlobalsUtils from '../utils/GlobalsUtils';
import { getLogger } from '../utils/SearchLogger';
import { PublicSearchManager } from '../services/PublicSearchManager';
import { Utils } from '../utils/Globals';
import {
	IFetchIntentShortcutSuggestions,
	IFetchRecentFiles,
	IFetchSAYTFiles,
	IResetSearch,
	IUpdateBotSuggestions,
	IUpdatePreSearchRecommendation,
} from '../actionTypes';
import SearchHostContext from '../utils/SearchHostContext';
import FileTypeService from '../services/FileTypeService';
import { SORT_BY_CONSTANTS, SORT_ORDER_CONSTANTS } from '../containers/SearchResultsPage/SearchConstants';
import { FontService } from '../services/FontService';
import AppService from '../services/AppService';
import LoggerService from 'src/utils/SearchLogger/LoggerService';

export const resetSearch =
	(searchHostContext: HostContext, logger: Logger) => (dispatch: (...args: Array<any>) => any) => {
		const userId = SearchUtils.getUserIDForStorage();
		const defaultSort = {
			sortBy: SORT_BY_CONSTANTS.modificationDate,
			sortOrder: SORT_ORDER_CONSTANTS.descending,
		};

		const defaultFilterState = {
			yourWorkFiltersSelected: false,
			yourWorkFiltersApplied: false,
			yourWorkFiltersCleared: true,
		};
		WebStorage.sessionStorage.setItem('yourWorkFilterState', defaultFilterState);
		WebStorage.sessionStorage.removeItem('yourWorkFilterOptions');
		WebStorage.sessionStorage.setItem('yourWorkSortState', { [userId]: defaultSort });
		WebStorage.sessionStorage.removeItem('searchData');
		WebStorage.sessionStorage.removeItem('previousSearchQuery');

		const dispatchPacket: IResetSearch = {
			type: ActionTypes.RESET_SEARCH,
			payload: {
				searchResultsPayload: {},
				searchResultsMeta: '',
				searchAssetsResultsPayload: {},
				searchAssetsResultsMeta: '',
				searchAutoCompleteItems: [],
				searchAutoCompleteTextSuggestions: [],
				searchAutoCompleteIntentTerms: [],
				searchAutoCompleteIntentShortcuts: [],
				exactTypedSearchQuery: '',
				searchQuery: '',
				tempSearchQuery: '',
				previousSearchQuery: '',
				showSearchDropdown: false,
				resetThumbsFeedback: false,
				submittedThumbsFeedback: false,
				submittedSearchQuery: false,
				yourWorkFiltersOptions: [],
				yourWorkFiltersSelected: false,
				yourWorkFiltersApplied: false,
				yourWorkFiltersCleared: true,
			},
		};

		dispatch(dispatchPacket);
	};

const getUSSContext = (searchHostContext: HostContext, currentTab: string) => {
	let section = '';
	let entitlements: string[] = [];
	let customerSegment = '';
	let planCodes: string[] = [];
	let planNames: string[] = [];
	let licensingStatus = '';
	const surface = searchHostContext.hostInfo.surface;
	const client = searchHostContext.hostInfo.client;
	let experiments: Array<Record<string, any>> = [];
	let hostExperiments: Array<Record<string, any>> = [];
	const floodgateExperiments = new FloodgateService(searchHostContext).getFloodgateCampaignsAndFeatures().campaigns;

	// This is collecting data to pass to USS for Contextual Parameters

	typeof currentTab === 'string' && (section = currentTab);
	if (searchHostContext.subscriptions && searchHostContext.subscriptions.length) {
		// FIXME: we are only returning the first subscription in the array
		// we should be returning the entire array
		const subscription = searchHostContext.subscriptions[0];
		customerSegment = subscription.customerSegment;
		entitlements = subscription.productEntitlements;
		// FIXME: don't return this as array when we fix this handling of subscription arrays
		planCodes = [subscription.planCode];
		planNames = subscription.planNames;
		licensingStatus = subscription.licensingStatus;
	}

	// Collect the list of experiments sent from the host
	if (
		GlobalsUtils.clientData &&
		GlobalsUtils.clientData.analyticData &&
		typeof GlobalsUtils.clientData.analyticData.experiment === 'object'
	) {
		const hostExperimentsTemp = GlobalsUtils.clientData.analyticData.experiment;
		hostExperiments = Object.keys(hostExperimentsTemp).map((campaignId) => ({
			// eslint-disable-next-line camelcase
			campaign_id: campaignId,
			variation_id: hostExperimentsTemp[campaignId], // eslint-disable-line camelcase
		}));
	}

	// Collect the host experiments and floodgate experiments
	// @ts-ignore
	experiments = hostExperiments.concat(...floodgateExperiments);
	return {
		// eslint-disable-next-line camelcase
		customer_segment: customerSegment,
		// eslint-disable-next-line camelcase
		licensing_status: licensingStatus,
		// eslint-disable-next-line camelcase
		plan_codes: planCodes,
		// eslint-disable-next-line camelcase
		plan_names: planNames,
		entitlements,
		application: {
			source: {
				name: client,
				surface,
				platform: client,
			},
		},
		section,
		experimentation: experiments,
	};
};

/**
 * Updates the store with the filtered array of items for the search dropdown.
 * @param {string} searchDropdownSection - Name of the search dropdown section.
 * @param {Array<string>} filteredSearchDropdownItems - Array of filtered search dropdown items.
 */
export const setFilteredSearchDropdownItems =
	(searchDropdownSection: string, filteredSearchDropdownItems: Array<string>) =>
	async (dispatch: (...args: Array<any>) => any) => {
		if (
			searchDropdownSection &&
			typeof searchDropdownSection === 'string' &&
			filteredSearchDropdownItems &&
			Array.isArray(filteredSearchDropdownItems)
		) {
			dispatch({
				type: ActionTypes.FILTER_SEARCH_DROPDOWN,
				payload: {
					searchDropdownSection,
					filteredSearchDropdownItems,
				},
			});
		}
	};

export const fetchPresearchRecentFiles =
	() =>
	async (dispatch: (...args: Array<any>) => any, getState: Function): Promise<void> => {
		let diff = false;
		if (SearchUtils.isSAYTFilesEnabled()) {
			const searchHostContext: HostContext = SearchHostContext.getContext();
			const logger = searchHostContext.logger;
			let recentFiles: Array<Record<string, any>> = [];

			const { search: currentState } = getState();
			const searchConfig = currentState?.searchConfig ?? {};

			if (currentState.searchQuery) {
				dispatch(setFilteredSearchDropdownItems('recentFiles', []));
				return;
			}

			try {
				let searchParams: any = {};
				if (searchHostContext.featureFlags.includes('ccsearch_enable_uss_contextual_parameters')) {
					searchParams = {
						ussContext: {},
					};
				}

				searchParams = {
					...searchParams,
					rendition: {
						size: {
							default: {
								image: 64,
								video: 64,
							},
						},
					},
					clientInfo: {
						surface: searchHostContext.hostInfo.surface,
						client: searchHostContext.hostInfo.client,
						// eslint-disable-next-line max-len
						clientVersion: `${searchHostContext.hostInfo.version.major}.${searchHostContext.hostInfo.version.minor}.${searchHostContext.hostInfo.version.revision}`,
					},
				};
				const searchService = new SearchService(searchHostContext);
				const recentCachePromise = searchService.fetchRecentFiles(searchParams, true, searchConfig);
				recentFiles = await recentCachePromise;

				const dispatchPayload: IFetchRecentFiles = {
					type: ActionTypes.FETCH_RECENT_FILES,
					payload: { matched: [], all: [], recent: recentFiles, query: '' },
				};

				dispatch(dispatchPayload);

				const fileIds = recentFiles?.map((file) => file.id) ?? [];
				dispatch(setFilteredSearchDropdownItems('recentFiles', [...fileIds]));

				const recentPromise = searchService.fetchRecentFiles(searchParams, false, searchConfig);
				const recentAPIResult = await recentPromise;
				const recentAPIFileIds = recentAPIResult?.map((file) => file.id) ?? [];

				diff = recentAPIResult?.some((file, index) => {
					const diffInId = recentFiles?.[index]?.id !== file.id;
					const diffInName = recentFiles?.[index]?.attributes?.name !== file?.attributes?.name;
					const diffInRendition = recentFiles?.[index]?.attributes?.openUrl !== file?.attributes?.openUrl;
					return diffInId || diffInName || diffInRendition;
				});

				if (diff) {
					const dispatchPayload: IFetchRecentFiles = {
						type: ActionTypes.FETCH_RECENT_FILES,
						payload: { matched: [], all: [], recent: recentAPIResult, query: '' },
					};

					dispatch(dispatchPayload);

					dispatch(setFilteredSearchDropdownItems('recentFiles', [...recentAPIFileIds]));
				}
			} catch (e: any) {
				logger?.('warn', `Presearch files action - recents: error in fetching files ${e.message}`);
			}

			logger?.('info', `Presearch files action - recents ${JSON.stringify({ recent: recentFiles?.length })}`);
		}

		return;
	};

/**
 * Fetches recent files for current logged in user.
 */
export const fetchFilesForAutocomplete =
	(fetchFiles?: boolean) =>
	async (dispatch: (...args: Array<any>) => any, getState: Function): Promise<void> => {
		if (SearchUtils.isSAYTFilesEnabled()) {
			const searchHostContext: HostContext = SearchHostContext.getContext();
			const logger = searchHostContext.logger;
			let recentFiles: Array<Record<string, any>> = [];
			let matchedFiles: Array<Record<string, any>> = [];
			let allFiles: Array<Record<string, any>> = [];

			const { search: currentState } = getState();
			const searchQuery = currentState?.searchQuery ?? '';
			const searchConfig = currentState?.searchConfig ?? {};
			const saytFilesState = currentState?.searchAutoCompleteFiles ?? { all: [], matched: [], query: '' };

			const env = searchHostContext.environment;
			const filesAutoCompleteConfig: any = searchConfig?.ui?.filesAutoComplete?.[env] ?? {};
			const limit: number = filesAutoCompleteConfig?.limit ?? 6;
			const minimumMatchLength: number = filesAutoCompleteConfig?.minimumMatchLength ?? 1;

			const searchQueryTokens = searchQuery.split(' ').map((token) => token.toLowerCase());

			try {
				let searchParams: any = {};
				let searchService;
				if (searchHostContext.featureFlags.includes('ccsearch_enable_uss_contextual_parameters')) {
					searchParams = {
						rendition: {
							size: {
								default: {
									image: 64,
									video: 64,
								},
							},
						},
						ussContext: {},
					};
				}

				if (searchQuery?.length) {
					searchParams = {
						...searchParams,
						clientInfo: {
							surface: searchHostContext.hostInfo.surface,
							client: searchHostContext.hostInfo.client,
							// eslint-disable-next-line max-len
							clientVersion: `${searchHostContext.hostInfo.version.major}.${searchHostContext.hostInfo.version.minor}.${searchHostContext.hostInfo.version.revision}`,
						},
					};
					const fromCache = !fetchFiles;
					searchService = new SearchService(searchHostContext);
					recentFiles = await searchService.fetchRecentFiles(searchParams, fromCache, searchConfig);
				}

				if (searchQuery.length >= minimumMatchLength) {
					searchParams.flags = [];
					const dispatchQueryPayload: IFetchSAYTFiles = {
						type: ActionTypes.FETCH_SAYT_FILES,
						payload: { query: searchQuery },
					};
					dispatch(dispatchQueryPayload);
					const flags = [];
					if (SearchUtils.isCCFilesEOLEnabled()) {
						flags.push('enableCCFilesEOL');
					}
					searchService
						?.fetchSearchAssetsResults({
							searchParams,
							query: searchQuery,
							filter: {},
							sort: {
								sortBy: 'ModificationDate',
								sortOrder: 'Descending',
							},
							startIndex: 0,
							limit,
							searchControl: {},
							flags,
						})
						.then((results) => {
							allFiles = results.getResultSources()[0].resultItems;
							logger?.(
								'info',
								`SAYT files action - all ${JSON.stringify({ all: allFiles?.length, matched: matchedFiles?.length })}`,
							);

							matchedFiles =
								searchQuery.length >= minimumMatchLength
									? [...recentFiles, ...allFiles]?.filter((file) =>
											searchQueryTokens.every((token) =>
												file?.attributes?.name?.toLowerCase()?.includes(token),
											),
										)
									: [];

							const dispatchPayload: IFetchSAYTFiles = {
								type: ActionTypes.FETCH_SAYT_FILES,
								payload: {
									matched: matchedFiles,
									all: [...allFiles],
									query: searchQuery,
								},
							};

							dispatch(dispatchPayload);

							const fileIds = matchedFiles?.map((file) => file.id) ?? [];
							dispatch(setFilteredSearchDropdownItems('files', [...fileIds]));

							return;
						});
				} else if (searchQuery.length < minimumMatchLength) {
					const dispatchPayload: IFetchSAYTFiles = {
						type: ActionTypes.FETCH_SAYT_FILES,
						payload: { all: [], query: searchQuery },
					};

					dispatch(dispatchPayload);
				}

				matchedFiles =
					searchQuery.length >= minimumMatchLength
						? [...recentFiles, ...saytFilesState.all]?.filter((file) =>
								searchQueryTokens.every((token) =>
									file?.attributes?.name?.toLowerCase()?.includes(token),
								),
							)
						: [];
			} catch (e: any) {
				logger?.('warn', `Search Actions SAYT: error in fetching files ${e.message}`);
			}

			logger?.(
				'info',
				`SAYT files action - recents ${JSON.stringify({ recent: recentFiles?.length, matched: matchedFiles?.length })}`,
			);

			const dispatchPayload: IFetchSAYTFiles = {
				type: ActionTypes.FETCH_SAYT_FILES,
				payload: { matched: matchedFiles, query: searchQuery },
			};

			dispatch(dispatchPayload);

			const fileIds = matchedFiles?.map((file) => file.id) ?? [];
			dispatch(setFilteredSearchDropdownItems('files', [...fileIds]));
		}

		return;
	};

/**
 * Fetches search results for the specified search query and locale and updates the store.
 * @param {string} query - The search query.
 * @param {string} locale - The locale. FIXME: it seems like we're getting passed a language code, rather than a locale token.
 */
export const fetchSearchResults =
	({
		query,
		locale,
		payload,
		currentTab,
		category,
	}: {
		query: string;
		locale: string;
		payload: searchRequestBody;
		currentTab: string;
		category?: string;
	}) =>
	async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		const searchHostContext = SearchHostContext.getContext();
		const logger = getLogger();
		const searchBodyFromConfig = payload;
		const authenticated = SearchHostContext.isAuthenticated();
		const { search: currentState } = getState();
		let res: Record<string, any> = {
			categoryOrder: [],
		};
		let trackingMetadata: Record<string, any>;
		const startTime = Date.now();
		if (!currentState?.searchConfig?.ui?.features?.search) {
			dispatch({
				type: ActionTypes.FETCH_SEARCH_RESULTS,
				payload: {
					query,
					...res,
					category,
				},
				meta: query,
			});
			return;
		}

		if (!query) {
			return;
		}

		let requestBody: Record<string, any>;
		const getAppsFromIntentModel = SearchUtils.isFloodgateFeatureInTest(
			'com.adobe.uss.ccs.appIntent',
			searchHostContext,
		);
		const enableNonCCSearchV3 = SearchUtils.isFloodgateFeatureInTest('ccs.apps.v3', searchHostContext);
		const clientPlatform = new Utils(searchHostContext).getPlatform();

		if (getAppsFromIntentModel) {
			/**
			 * https://jira.corp.adobe.com/browse/CCS-3297
			 * We need to do two things here
			 * 1. Set qis to false
			 * 2. Setup apps to get data from USS CCEXP
			 */

			searchBodyFromConfig.qisV2 && (searchBodyFromConfig.qisV2 = false);
			Array.isArray(searchBodyFromConfig.searchBodies) &&
				searchBodyFromConfig.searchBodies.forEach((searchBody) => {
					const searchBodyId = searchBody?.id || '';
					if (searchBodyId.toLowerCase() === 'appcards') {
						searchBody.metadata = Object.assign({}, searchBody.metadata, { skipQueryExecution: false });
					}
				});
		}

		if (enableNonCCSearchV3) {
			searchBodyFromConfig.enableNonCCAppsSRP = true;
		}
		requestBody = Object.assign({}, searchBodyFromConfig);

		requestBody = Object.assign(requestBody, {
			q: query,
			limit: 10,
			locale: locale || 'en',
			clientInfo: {
				surface: searchHostContext.hostInfo.surface,
				client: searchHostContext.hostInfo.client,
				// eslint-disable-next-line max-len
				clientVersion: `${searchHostContext.hostInfo.version.major}.${searchHostContext.hostInfo.version.minor}.${searchHostContext.hostInfo.version.revision}`,
			},
		});
		requestBody = Object.assign(requestBody, { platform: clientPlatform });

		if (searchHostContext.featureFlags.includes('ccsearch_enable_uss_contextual_parameters')) {
			requestBody = Object.assign(requestBody, {
				ussContext: getUSSContext(searchHostContext, currentTab),
			});
		}

		const enableQuickActionSearch = SearchUtils.isFloodgateFeatureInTest(
			'ccs.ui.srp.quickaction',
			searchHostContext,
		);
		if (!enableQuickActionSearch) {
			delete requestBody.quickActions;
		}

		const searchForACategory = Boolean(category);
		const fontsCategory = requestBody.fonts || category?.toLowerCase() === 'fonts';

		const promises = [];
		promises.push(SearchUtils.getSearchAppMetadataFromSessionStorage(searchHostContext));
		promises.push(new PublicSearchManager(searchHostContext, logger).search(requestBody, { authenticated }));
		// fontsCategory && authenticated && promises.push();

		const allResults = await GlobalsUtils.checkAndExecutePromiseAllSettledPolyfill(promises);
		const resultApps = allResults[0].status === 'fulfilled' ? allResults[0].value : allResults[0].reason;

		if (allResults[1].status === 'fulfilled') {
			// trackingMetadata = allResults[1].value.metadata;
			// const fontItem = allResults[1]?.value?.data?.find((v) => v.id === 'fonts');
			// const fontDataResults = allResults[2] ? allResults[2] : [];
			// if (fontItem && fontDataResults.status && fontDataResults.status === 'fulfilled') {
			// 	fontItem.searchResults.resultItems = fontDataResults.value;
			// 	fontItem.searchResults.totalResults = fontDataResults.value.length;
			// }
			// trackingMetadata = allResults[1].value.metadata;
			res = allResults[1].value;
		} else if (allResults[1].status === 'rejected') {
			res = allResults[1].reason;
		}

		const duration = Date.now() - startTime;
		logger.log('Search Actions', `Search Data Action Total: duration=${duration}`);
		// Save the timing data to session storage
		SearchUtils.saveSearchTimingInSessionStorage('search', 'action', duration);

		dispatch({
			type: ActionTypes.FETCH_SEARCH_RESULTS,
			payload: {
				query,
				...res,
				category,
			},
			meta: {
				pagination: {
					timestamp: new Date().getTime(),
				},
			},
		});

		if (fontsCategory && authenticated) {
			dispatch(fetchFontSearchResults(query));
		}

		if (searchForACategory) {
			const categoryData = SearchUtils.getSearchCategoryData(res.data as SearchCategoryResult[], category);
			return categoryData;
		}
		return;
	};

/**
 * Fetches search results for the specified search query and locale and updates the store.
 * @param {string} query - The search query.
 * @param {string} locale - The locale. FIXME: it seems like we're getting passed a language code, rather than a locale token.
 */
export const fetchPresearchData =
	(searchHostContext: HostContext, logger: Logger, locale: string, isCDNEnabledFromFloodgate: boolean) =>
	async (dispatch: (...args: Array<any>) => any) => {
		const authenticated = SearchHostContext.isAuthenticated();
		let res: Record<string, any> = {
			categoryOrder: [],
		};
		const startTime = Date.now();
		let sections;
		if (!isCDNEnabledFromFloodgate) {
			sections = {
				expressQuickActions: true,
				popularSearches: true,
				intentToActions: true,
			};
		} else {
			sections = {
				expressQuickActions: true,
				intentToActions: true,
			};
		}
		const { major = '0', minor = '0', revision = '0' } = searchHostContext?.hostInfo?.version || {};
		const clientVersion = `${major}.${minor}.${revision}`;
		let preSearchRequestJson;
		if (!isCDNEnabledFromFloodgate) {
			preSearchRequestJson = {
				locale: locale || 'en',
				clientInfo: {
					surface: searchHostContext.hostInfo.surface,
					client: searchHostContext.hostInfo.client,
					// eslint-disable-next-line max-len
					clientVersion,
				},
				ussContext: {},
				sections,
				subscriptions: searchHostContext?.subscriptions ?? [],
			};

			if (searchHostContext.featureFlags.includes('ccsearch_enable_uss_contextual_parameters')) {
				preSearchRequestJson = {
					...preSearchRequestJson,
					ussContext: getUSSContext(searchHostContext, ''),
				};
			}
		} else {
			preSearchRequestJson = {
				locale: locale || 'en',
				clientInfo: {
					surface: searchHostContext.hostInfo.surface,
					client: searchHostContext.hostInfo.client,
					// eslint-disable-next-line max-len
					clientVersion,
				},
				sections,
			};
		}

		try {
			res = await new PublicSearchManager(searchHostContext, logger).getPreSearchData(preSearchRequestJson, {
				authenticated,
			});
			SearchUtils.createIntentDictionary(res?.data?.intentToActions?.searchResults?.resultItems ?? []);
		} catch (e) {
			res = e;
		} finally {
			const duration = Date.now() - startTime;
			logger.log('Search Actions', `PreSearch Data Action Total: duration=${duration}`);
			// Save the timing data to session storage
			SearchUtils.saveSearchTimingInSessionStorage('presearch', 'action', duration);
		}

		dispatch({
			type: ActionTypes.FETCH_PRESEARCH_RESULTS,
			payload: res,
		});
		const expressRecommendations = [];
		res?.data?.expressQuickActions?.searchResults?.resultItems.length > 0 &&
			expressRecommendations.push('expressQuickActions');
		res?.data?.popularExpressTemplates?.searchResults?.resultItems.length > 0 &&
			expressRecommendations.push('popularExpressTemplates');
		dispatch(setFilteredSearchDropdownItems('searchRecommendations', expressRecommendations));
	};
/**
 * Fetches search assets results for the specified search query and locale and updates the store.
 * @param {string} query - The search query.
 * @param {string} locale - The locale. FIXME: it seems like we're getting passed a language code, rather than a locale token.
 * @param {?FilterItems} filter - the filter parameters. If none provided, we pass in an empty filter set.
 * @param {?Sort} sort - the sort parameters. If none provided, we pass in sort by modification date, ascending.
 */

type fetchSearchAssetsResultsArgs = {
	searchHostContext: HostContext;
	logger: Logger;
	query: string;
	locale: string;
	currentTab: string;
	searchConfig: Record<string, any>;
	filter?: SelectedUiFilters;
	sort?: Sort;
};
export const fetchSearchAssetsResults =
	({ searchHostContext, logger, query, currentTab, searchConfig, filter = {}, sort }: fetchSearchAssetsResultsArgs) =>
	async (dispatch: (...args: Array<any>) => any) => {
		const authenticated = SearchHostContext.isAuthenticated();
		let res = {};

		const startTime = Date.now();
		if (!searchConfig?.ui?.features?.searchAssets) {
			dispatch({
				type: ActionTypes.FETCH_SEARCH_ASSETS_RESULTS,
				payload: res,
				meta: query,
			});
			return;
		}

		if (authenticated && query) {
			// Guy says that we won't have search-asset request bodies in the search server's configuration data
			// We make separate search-assets calls for each USS `source` type.
			// Legal values for `source` are: 'creativeCloud', 'lightroom', 'communityPlatform', 'sharedWithMe'.
			// SearchService will populate `source` for each of the USS searchables that it creates.
			let searchAssetsRequestJsonTypeV1 = {
				rendition: {
					size: {
						default: {
							image: 360,
							video: 240,
						},
					},
				},
				ussContext: {},
			};
			// Initial search start index and limit
			const startIndex = 0;
			const limit = 50;
			const emptyFilter = {};
			// Get our initial sets of Filters + Sorts (the default state, no filters, initial sort by modification date in descending order).
			// NOTE: The YourWorkDataSourceUtils.js constructor also does the same, but, we are reading the stored sorting data and initializing it if not present. Hence, we need it here also.
			const currentSort = YourWorkUtils.fetchCurrentSortOrder();
			const searchService = new SearchService(searchHostContext);

			if (searchHostContext.featureFlags.includes('ccsearch_enable_uss_contextual_parameters')) {
				searchAssetsRequestJsonTypeV1 = {
					rendition: {
						size: {
							default: {
								image: 360,
								video: 240,
							},
						},
					},
					ussContext: {},
				};
				searchAssetsRequestJsonTypeV1 = Object.assign(searchAssetsRequestJsonTypeV1, {
					ussContext: getUSSContext(searchHostContext, currentTab),
				});
			}

			const searchControl = searchConfig.searchControl || {};
			const combinedSearchConfig: Record<string, any> = { ...searchAssetsRequestJsonTypeV1 };

			combinedSearchConfig.clientInfo = {
				surface: searchHostContext.hostInfo.surface,
				client: searchHostContext.hostInfo.client,
				// eslint-disable-next-line max-len
				clientVersion: `${searchHostContext.hostInfo.version.major}.${searchHostContext.hostInfo.version.minor}.${searchHostContext.hostInfo.version.revision}`,
			};

			const flags = [];
			if (SearchUtils.isCCFilesEOLEnabled()) {
				flags.push('enableCCFilesEOL');
			}

			res = await searchService.fetchSearchAssetsResults({
				searchParams: combinedSearchConfig,
				query,
				filter: filter || emptyFilter,
				sort: sort || currentSort,
				startIndex,
				limit,
				searchControl,
				flags,
			});
			if (res instanceof SearchResults) {
				logger.log('Search Actions', 'ywds total', res.getTotalResults());

				// Cache this INITIAL set of search-assets results...as we get more results (via Infinite Scroll), they will be added to this cache.
				// Only perform this if ALL of the ResultSources in this SearchResults object are valid (one would be invalid if it errored).
				if (res.resultSources.reduce(async (acc, curr) => acc && curr.resultItems, {})) {
					window.searchAssetsItemAccumulator = await res.getNResults(limit); // Requested amount avoids a fetch of additional data
				}
			}

			const duration = Date.now() - startTime;
			logger.log('Search Actions', `Search Assets Action Total: duration=${duration}`);
			// Save the timing data to session storage
			SearchUtils.saveSearchTimingInSessionStorage('search-assets', 'action', duration);
		}

		dispatch({
			type: ActionTypes.FETCH_SEARCH_ASSETS_RESULTS,
			payload: res,
			meta: query,
		});
	};

/**
 * Updates the store with the array of search history items.
 * @param {Array<string>} searchHistoryItems - Array of search history items.
 */
export const setSearchHistoryItems =
	(searchHistoryItems: Array<string>) => async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		if (searchHistoryItems && Array.isArray(searchHistoryItems)) {
			const { search: currentState } = getState();
			dispatch({
				type: ActionTypes.SET_SEARCH_HISTORY,
				payload: searchHistoryItems,
			});
			dispatch(
				setFilteredSearchDropdownItems(
					'searchHistory',
					SearchUtils.getFilteredSearchHistoryItems(currentState.searchQuery, searchHistoryItems),
				),
			);
		}
	};

/**
 * Fetches the list of search history items and updates the store.
 */
export const fetchSearchHistoryItems =
	(searchHostContext: HostContext) => async (dispatch: (...args: Array<any>) => any) => {
		const authenticated = SearchHostContext.isAuthenticated();
		let res = [];

		try {
			if (authenticated && searchHostContext.featureFlags.includes('temp_enable_search_history_server')) {
				const searchService = new SearchService(searchHostContext);
				const searchServerSearchHistoryItemsRaw = (await searchService.fetchSearchHistory(
					50,
				)) as searchServerSearchHistoryItemsRaw;
				const searchServerSearchHistoryItems = {
					searches: searchServerSearchHistoryItemsRaw.searches,
				};
				const searchHistoryItems =
					SearchUtils.processSearchServerSearchHistoryItems(searchServerSearchHistoryItems);
				res = searchHistoryItems;
				SearchUtils.setHistoryDataToSessionStorage(searchHistoryItems);
			} else {
				res = SearchUtils.getHistoryDataFromSessionStorage();
			}
		} catch (e) {
			res = SearchUtils.getHistoryDataFromSessionStorage();
		}

		dispatch({
			type: ActionTypes.FETCH_SEARCH_HISTORY,
			payload: res,
		});

		dispatch(setSearchHistoryItems(res));
	};

/**
 * Sets the Search Dropdown current section
 * @param {string} sectionName - Name of the current search dropdown section.
 */
export const setSearchDropdownCurrentSectionItem =
	(sectionItem: string) => async (dispatch: (...args: Array<any>) => any) => {
		dispatch({
			type: ActionTypes.SET_SEARCH_DROPDOWN_CURRENT_SECTION_ITEM,
			payload: sectionItem,
		});
	};

/**
 * Fetches the list of App metaData items and stores it the session storage.
 * @param {HostContext} searchHostContext - The search hostContext.
 * @param {Logger} logger
 */
export const fetchAppMetadataList =
	(searchHostContext: HostContext, updateAppStatus: boolean = false) =>
	async (dispatch: (...args: Array<any>) => any) => {
		let res: SearchAppMetadata = await SearchUtils.getSearchAppMetadataFromSessionStorage(searchHostContext);
		res.timestamp = new Date().toISOString();
		dispatch({
			type: ActionTypes.FETCH_SEARCH_APP_METADATA,
			payload: res,
		});

		if (updateAppStatus) {
			res.timestamp = new Date().toISOString();
			res = await new AppService(searchHostContext).setAppsStatusData(res);
			dispatch({
				type: ActionTypes.FETCH_SEARCH_APP_METADATA,
				payload: res,
			});
		}
	};

export const fetchOnboardingSummaryItems =
	(searchHostContext: HostContext, searchQuery: string) =>
	async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		let res: Record<string, any> = {
			leoOnboardingSummaryItems: [],
		};

		const authenticated = SearchHostContext.isAuthenticated();
		const hostFilesTabActive = SearchUtils.isHostFilesPanelActive();
		if (!authenticated || hostFilesTabActive) {
			return;
		}

		try {
			const searchService = new SearchService(searchHostContext);
			const { search: currentState } = getState();
			const searchConfig = currentState?.searchConfig ?? {};
			const onboardingCompletePromise = searchService.fetchLeoOnboarding(searchQuery, searchConfig) as Promise<
				Record<string, any>
			>;

			const leoOnboardingSummaryItems: Record<string, any> = await onboardingCompletePromise;
			res = {
				leoOnboardingSummaryItems,
			};
		} catch (e) {
			getLogger().error('action', 'error in fetchOnboardingSummaryItems', e);
		}

		dispatch({
			type: ActionTypes.FETCH_SEARCH_GEN_SUMMARY,
			payload: res,
		});
		dispatch(setFilteredSearchDropdownItems('leoOnboardingSummaryItems', res.leoOnboardingSummaryItems));
		return;
	};

export const storeUserPreferences =
	(searchHostContext: HostContext, genAiPreference: boolean) =>
	async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		let res: Record<string, any> = {};
		let result = { status: false, showGenAIResponses: false };
		const authenticated = Object.values(searchHostContext.imsInfo).filter(Boolean).length > 0;
		if (!authenticated) {
			return result;
		}

		try {
			const searchService = new SearchService(searchHostContext);
			res = {
				storingUserPreferences: true,
			};
			dispatch({
				type: ActionTypes.STORE_USER_PREFERENCES,
				payload: res,
			});
			(await searchService.storeUserPreferences(genAiPreference)) as Promise<Record<string, any>>;

			res = {
				storingUserPreferences: false,
				showGenAIResponses: genAiPreference,
			};
			result = { status: true, showGenAIResponses: genAiPreference };
		} catch (e) {
			getLogger().error('action', 'error in storeUserPreferences', e);
			res = {
				storingUserPreferences: false,
			};
		}

		dispatch({
			type: ActionTypes.STORE_USER_PREFERENCES,
			payload: res,
		});
		return result;
	};

export const fetchingGenAIResults =
	(fetchingGenAiResult: boolean) => async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		const res: Record<string, any> = {
			fetchingGenAiResult,
		};
		dispatch({
			type: ActionTypes.FETCH_SEARCH_GEN_SUMMARY_IN_PROGRESS,
			payload: res,
		});
	};
export const fetchUserPreferences = () => async (dispatch: (...args: Array<any>) => any, getState: Function) => {
	let res: Record<string, any> = {
		showGenAIResponses: false,
	};

	const authenticated = SearchHostContext.isAuthenticated();
	if (!authenticated) {
		return;
	}
	try {
		const searchService = new SearchService(SearchHostContext.getContext());
		const userPreferencesPromise = (await searchService.fetchUserPreferences()) as Promise<Record<string, any>>;

		const userPreferences: Record<string, any> = await userPreferencesPromise;
		res = {
			showGenAIResponses: userPreferences.genAIOptIn,
		};
	} catch (e) {
		getLogger().error('action', 'error in fetchUserPreferences', e);
	}

	dispatch({
		type: ActionTypes.FETCH_USER_PREFERENCES,
		payload: res,
	});
};
/**
 * Fetches the list of search auto suggestions for the specified search query and updates the store.
 * @param {string} searchQuery - The search query.
 */
export const fetchSearchAutoCompleteItems =
	(searchHostContext: HostContext, searchQuery: string) =>
	async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		// move this out of the if block to reset autocomplete when query length is 1
		const searchAutoCompleteLoadedAt = new Date().getTime();
		let res: Record<string, any> = {
			searchAutoCompleteItems: [],
			searchAutoCompleteTextSuggestions: [],
			searchAutoCompleteLoadedAt,
		};
		const { search: currentState } = getState();
		const searchConfig = currentState?.searchConfig ?? {};
		try {
			if (searchQuery && searchQuery.length > 1) {
				// Fetch auto suggestions from the search server or from USS.
				// TODO: Check if all surfaces are sending this if yes remove this
				const searchAutoSuggestionFeatureFlag = searchHostContext.featureFlags.includes(
					'temp_enable_search_auto_suggestion_server_uss',
				);

				const searchService = new SearchService(searchHostContext);
				const searchAppMetadataList = currentState.searchAppMetadataList;
				const filteredSearchDropdownItems = currentState.filteredSearchDropdownItems;
				const searchAutoCompletePromise = (
					searchAutoSuggestionFeatureFlag
						? searchService.fetchSearchAutoComplete(searchQuery, searchConfig)
						: Promise.resolve({})
				) as Promise<Record<string, any>>;

				const searchAutoCompleteData: Record<string, any> = await searchAutoCompletePromise;
				const processedSearchAutoCompleteData =
					SearchUtils.processSearchServerSearchAutoCompleteItems(searchAutoCompleteData);

				const enableAppQuickLinks = true || searchConfig?.ui?.features?.appQuickLinks;
				const searchAutoCompleteItems = enableAppQuickLinks
					? SearchUtils.getAutoCompleteQuickLinkApps(
							searchHostContext,
							searchAppMetadataList,
							processedSearchAutoCompleteData,
						)
					: [];

				const searchAutoCompleteTextSuggestions = SearchUtils.removeSearchAutoCompleteDuplicates(
					processedSearchAutoCompleteData,
					filteredSearchDropdownItems.searchHistory,
					filteredSearchDropdownItems.searchTrending,
				);

				res = {
					searchAutoCompleteItems,
					searchAutoCompleteTextSuggestions,
					searchAutoCompleteLoadedAt,
					searchAutoCompleteQuery: searchQuery,
				};
			}
		} catch (e) {
			getLogger().error('action', 'fetchSearchAutoCompleteItems', e);
		}

		dispatch({
			type: ActionTypes.FETCH_SEARCH_AUTO_SUGGESTION,
			payload: res,
		});

		const quickLinksAppNames = res.searchAutoCompleteItems.map((app) => app.name);
		dispatch(setFilteredSearchDropdownItems('searchApps', [...quickLinksAppNames]));
		dispatch(setFilteredSearchDropdownItems('searchAutoComplete', res.searchAutoCompleteTextSuggestions));

		return;
	};

export const fetchIntentShortcuts =
	(searchHostContext: HostContext, searchQuery: string) =>
	async (dispatch: (...args: Array<any>) => any, getState: Function): Promise<void> => {
		const authenticated = SearchHostContext.isAuthenticated();
		let res: Array<AppResultItem> = [];

		try {
			if (searchQuery) {
				const searchService = new SearchService(searchHostContext);
				const { search: currentState } = getState();
				const searchAppMetadataList = currentState.searchAppMetadataList;
				const searchConfig = currentState?.searchConfig ?? {};
				const env = searchHostContext.environment;
				const locale = searchHostContext.locale.toLowerCase();
				const autoCompleteConfig: any = searchConfig?.ui?.autoComplete?.[env] ?? {};
				const minimumMatchLength: number = autoCompleteConfig?.intent?.minimumMatchLength ?? 3;
				const localeMinMatchLength: number = autoCompleteConfig?.intent?.localeMinMatchLength?.[locale] ?? 0;

				if (searchQuery.length >= (localeMinMatchLength || minimumMatchLength)) {
					const intentToActions =
						currentState?.presearchResultsPayload?.intentToActions?.searchResults?.resultItems ?? {};
					const searchAutoCompleteIntentsPromise: Promise<Intent[]> =
						searchService.fetchSearchIntentsAutoComplete(
							searchQuery,
							searchConfig,
							SearchUtils.dictonaries.searchIntentDictionary,
						) as Promise<Array<Intent>>;

					const searchAutoCompleteShortCuts: any = await searchAutoCompleteIntentsPromise;

					const filteredIntentShortcuts = [...(searchAutoCompleteShortCuts ?? [])];

					res = SearchUtils.getAppsForIntentShortcuts(searchAppMetadataList, filteredIntentShortcuts);
				} else {
					const I2ASessionData = WebStorage.sessionStorage.getItem('asde.autocomplete.I2A') as Record<
						string,
						any
					>;
					I2ASessionData.queries = {};
					WebStorage.sessionStorage.setItem('asde.autocomplete.I2A', I2ASessionData);
				}
			}
		} catch (e) {
			getLogger().log('action', 'error in fetchIntentShortcuts', e);
		}
		const shortcutNames = res.map((app) => app.name);

		const dispatchPayload: IFetchIntentShortcutSuggestions = {
			type: ActionTypes.FETCH_INTENT_SHORTCUT_SUGGESTION,
			payload: { searchAutoCompleteIntentShortcuts: res, searchAutoCompleteIntentTerms: shortcutNames },
		};

		dispatch(dispatchPayload);

		dispatch(setFilteredSearchDropdownItems('intentShortcuts', [...shortcutNames]));

		return;
	};

/**
 * Updates the store with the search query previously used.
 * @param {string} previousSearchQuery - The search query previously used.
 */
export const setPreviousSearchQuery =
	(previousSearchQuery: string) => async (dispatch: (...args: Array<any>) => any) => {
		dispatch({
			type: ActionTypes.SET_PREVIOUS_SEARCH_QUERY,
			payload: previousSearchQuery,
		});
	};

/**
 * Updates the store with the submitted search query.
 * @param {string} submittedSearch - The submitted search query.
 */
export const setSubmittedSearchQuery = (submittedSearch: boolean) => async (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SUBMITTED_SEARCH_QUERY,
		payload: submittedSearch,
	});
};

/**
 * Updates the store with the search query.
 * @param {string} searchQuery - The search query.
 */
export const setSearchQuery = (searchQuery: string) => async (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SEARCH_QUERY,
		payload: searchQuery,
	});
};

/**
 * Updates the store with the exact search query.
 * @param {string} exactTypedSearchQuery - The exact typed search query.
 */
export const setExactTypedSearchQuery =
	(typedSearchQuery: string) => async (dispatch: (...args: Array<any>) => any) => {
		dispatch({
			type: ActionTypes.EXACT_TYPED_SEARCH_QUERY,
			payload: typedSearchQuery,
		});
	};

/**
 * Updates the store with the temp search query.
 * @param {string} tempSearchQuery - The temp search query.
 */
export const setTempSearchQuery = (tempSearchQuery: string) => async (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.TEMP_SEARCH_QUERY,
		payload: tempSearchQuery,
	});
};

/**
 * Updates the store with the display state of the search dropdown.
 * @param {boolean} value - The current display state of the search dropdown.
 */
export const showSearchDropdown =
	(hostShowSearchDropdown: boolean, value: boolean) => async (dispatch: (...args: Array<any>) => any) => {
		Utils.dropDownShowing = value;

		if (typeof hostShowSearchDropdown === 'undefined' || hostShowSearchDropdown === true) {
			// The event is sent because the host app needs to
			// know the state of the drop down. This is so handling things
			// like the escape are handled correctly.
			const customEvent = new CustomEvent('setCCSearchDropdownState', {
				detail: {
					dropStatus: value,
				},
			});

			if (!value) {
				// If we are closing the drop down, delay the
				// event so we handle the escape key correctly.
				setTimeout(() => {
					window.dispatchEvent(customEvent);
				}, 1000);
			} else {
				window.dispatchEvent(customEvent);
			}
		} else {
			const customEvent = new CustomEvent('setCCSearchDropdownState', {
				detail: {
					dropStatus: false,
				},
			});
			window.dispatchEvent(customEvent);
		}
		dispatch({
			type: ActionTypes.SHOW_SEARCH_DROPDOWN,
			payload: value,
		});
	};

/**
 * Updates the store to reset the state of the feedback.
 * @param {boolean} value - The reset state of the feedback.
 */
export const resetThumbsFeedback = (value: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.RESET_THUMBS_FEEDBACK,
		payload: value,
	});
};

/**
 * Updates the store with the display state of the feedback.
 * @param {boolean} value - The display state of the feedback.
 */
export const displaySearchThumbsFeedback = (value: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SHOW_SEARCH_THUMBS_FEEDBACK,
		payload: value,
	});
};

/**
 * Updates the store with the current search view.
 * @param {string} value - The current search view.
 */
export const setCurrentSearchView = (value: string) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.CURRENT_SEARCH_VIEW,
		payload: value,
	});
};

export const updateLeftnavStatus = (value: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.UPDATE_LEFTNAV_STATUS,
		payload: value,
	});
};

/**
 * Updates the store with the submit state of the search feedback.
 * @param {boolean} value - The submit state of the search feedback.
 */
export const setSubmittedSearchThumbsFeedback = (value: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SUBMITTED_THUMBS_FEEDBACK,
		payload: value,
	});
};

/**
 * Updates the store with the current tab for the SRP.
 * @param {number} value - The index of the current SRP tab.
 */
export const setSRPCurrentTab = (value: number) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SET_SRP_CURRENT_TAB,
		payload: value,
	});
};

/**
 * Updates the store with the filter options for the Your Works filters.
 * @param {Array<Object>} filterOptions
 */
export const setYourWorkFiltersOptions =
	(filterOptions: YourWorkFilters) => (dispatch: (...args: Array<any>) => any) => {
		dispatch({
			type: ActionTypes.SET_YOUR_WORK_FILTERS,
			payload: filterOptions,
		});
	};

/**
 * Updates the store with whether or not the user selected a Your Work filter item from any category.
 * @param {bool} filtersSelected
 */
export const setYourWorkFiltersSelected = (filtersSelected: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SET_YOUR_WORK_FILTERS_SELECTED,
		payload: filtersSelected,
	});
};

/**
 * Updates the store with whether or not the user applied the total Your Work filters.
 * @param {bool} filtersApplied
 */
export const setYourWorkFiltersApplied = (filtersApplied: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SET_YOUR_WORK_FILTERS_APPLIED,
		payload: filtersApplied,
	});
};

/**
 * Updates the store with whether or not the user cleared the total Your Work filters.
 * @param {bool} filtersCleared
 */
export const setYourWorkFiltersCleared = (filtersCleared: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SET_YOUR_WORK_FILTERS_CLEARED,
		payload: filtersCleared,
	});
};

/**
 * Updates the store with whether or not the user show/hide the Your Work filters.
 * @param {bool} filtersDisplay
 */
export const setYourWorkFiltersDisplay = (showFilters: boolean) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SET_YOUR_WORK_FILTERS_DISPLAY,
		payload: showFilters,
	});
};

/**
 * Sets the theme of the app. Theme can be 'light' or 'dark'.
 * @param {string} theme
 */
export const setTheme = (theme: string) => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SET_THEME,
		payload: theme,
	});
};

/**
 * Updates the store with the total count of search results by individual tab.
 * @param {bool} searchResultsTotalCount
 */
export const setSearchResultsTotalCountByTab =
	(tab: number | string, totalCount?: number) => (dispatch: (...args: Array<any>) => any) => {
		dispatch({
			type: ActionTypes.SET_SEARCH_RESULTS_TOTAL_COUNT_BY_TAB,
			payload: {
				tab,
				totalCount,
			},
		});
	};

/**
 * Set chips/tags to be shown for filter selected
 * @param selectedFilters The filter options selected
 * @returns
 */
export const setYourWorkFilterChips = () => (dispatch: (...args: Array<any>) => any) => {
	dispatch({
		type: ActionTypes.SET_YOUR_WORK_FILTER_CHIPS,
	});
};

/**
 * Called whenever any filter checkbox/radio is clicked
 * @param index Index of filter option being updated
 * @param filterOption The filter item which was clicked and updated
 * @returns
 */
export const updateSelectedYourWorkFilterOption =
	(index: number, filterOption: YourWorkFilterOption) => (dispatch: (...args: Array<any>) => any) => {
		dispatch({
			type: ActionTypes.UPDATE_SELECTED_FILTER_OPTIONS,
			payload: {
				index,
				filterOption,
			},
		});
	};

export const updatePreSearchRecommendations =
	(recommendations: Record<string, any>) => (dispatch: (...args: Array<any>) => any, getState: Function) => {
		const env = SearchHostContext.getContext().environment;
		const { search: currentState } = getState();
		const searchAppMetadataList = currentState.searchAppMetadataList;
		const preSearchRecommendationTranslations =
			FileTypeService.fileTypeDataObject?.translations?.preSearchRecommendations || {};
		// filter out the disabled entries

		const groupedPreSearchRecommendations: Record<string, Array<any>> = recommendations[env].reduce(
			(group, recommendation: any) => {
				const translatedRecommendation: Record<string, any> = {};
				// translate all keys on first level nesting
				Object.keys(recommendation).forEach((key) => {
					if (preSearchRecommendationTranslations[recommendation[key]]) {
						translatedRecommendation[key] = preSearchRecommendationTranslations[recommendation[key]];
					} else {
						translatedRecommendation[key] = recommendation[key];
					}
				});
				// translate all keys on second level nesting i.e. action
				Object.keys(recommendation.action).forEach((key) => {
					if (preSearchRecommendationTranslations[recommendation.action[key]]) {
						translatedRecommendation.action[key] =
							preSearchRecommendationTranslations[recommendation.action[key]];
					} else {
						translatedRecommendation.action[key] = recommendation.action[key];
					}
				});
				const recommendationGroupType = recommendation?.recommendationType?.toLowerCase() || 'unknown';
				group[recommendationGroupType] = group[recommendationGroupType] || [];
				group[recommendationGroupType].push(translatedRecommendation);

				return group;
			},
			{},
		) || {};

		let allRecommendations = [];

		Object.entries(groupedPreSearchRecommendations).forEach(
			([group, recommendations]: [string, Array<Record<string, any>>]) => {
				groupedPreSearchRecommendations[group] = recommendations
					.filter((recommendation: any) => !('disabled' in recommendation && recommendation.disabled))
					.sort((a: Record<string, any>, b: Record<string, any>) => a.score - b.score);

				allRecommendations = [...allRecommendations, ...groupedPreSearchRecommendations[group]];
			},
		);

		const dispatchPacket: IUpdatePreSearchRecommendation = {
			type: ActionTypes.UPDATE_PRE_SEARCH_RECOMMENDATIONS,
			payload: {
				recommendations: groupedPreSearchRecommendations,
			},
		};
		dispatch(dispatchPacket);
		const expressRecommendations = [];
		currentState?.presearchResultsPayload?.expressQuickActions?.searchResults?.resultItems.length > 0 &&
			expressRecommendations.push('expressQuickActions');
		currentState?.presearchResultsPayload?.popularExpressTemplates?.searchResults?.resultItems.length > 0 &&
			expressRecommendations.push('popularExpressTemplates');

		dispatch(
			setFilteredSearchDropdownItems('searchRecommendations', [
				...SearchUtils.getFilteredSearchDropDownRecommendationItems(allRecommendations, searchAppMetadataList),
				...expressRecommendations,
			]),
		);
	};

export const resetBotSuggestions = () => async (dispatch: (...args: Array<any>) => any) => {
	const botSuggestions: Array<BotSuggestion> = [];
	const dispatchPacket: IUpdateBotSuggestions = {
		type: ActionTypes.UPDATE_BOT_SUGGESTIONS,
		payload: {
			botSuggestions,
		},
	};
	dispatch(dispatchPacket);
	return Promise.resolve(true);
};

export const fetchBotSuggestionsForQuery =
	(query: string) => async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		const { search: currentState } = getState();
		const searchConfig = currentState?.searchConfig ?? {};
		const bot = new BotService(SearchHostContext.getContext());
		let botSuggestions: Array<BotSuggestion> = [];

		const queryResult: BotQueryResult | Error = await bot.query(query, searchConfig);
		if (!(queryResult instanceof Error)) {
			botSuggestions = queryResult.data;
		}
		const dispatchPacket: IUpdateBotSuggestions = {
			type: ActionTypes.UPDATE_BOT_SUGGESTIONS,
			payload: {
				botSuggestions,
			},
		};
		dispatch(dispatchPacket);
	};

export const fetchFontSearchResults =
	(query: string) => async (dispatch: (...args: Array<any>) => any, getState: Function) => {
		try {
			dispatch({
				type: ActionTypes.SET_FONT_SEARCH_RESULTS,
				payload: [],
				loading: true,
			});
			const searchHostContext = SearchHostContext.getContext();
			await FontService.loadDependencies();
			const fontPromise: Promise<Array<any>> = new FontService(searchHostContext).searchFonts(query);
			const fontSearchResults = await fontPromise;
			dispatch({
				type: ActionTypes.SET_FONT_SEARCH_RESULTS,
				payload: fontSearchResults,
				loading: false,
			});
		} catch (error) {
			getLogger().error('Action: fetchFontSearchResults', 'Font Search Error', error);
		}
	};

export const setSourceOfQuery = (source: string) => (dispatch: (...args: Array<any>) => any) => {
	// This is hack for the case where the search result page is totally different from the search bar page
	// and we need to set the source of query to the search result page e.g. HelpX
	WebStorage.localStorage.setItem('sourceOfQueryInSearch', source);
	dispatch({
		type: ActionTypes.SET_SOURCE_OF_QUERY,
		payload: source,
	});
};

export const fetchQueryIntent = (query: string) => async (dispatch: (...args: Array<any>) => any) => {
	try {
		const isLeoEnabledFromFloodgate = SearchUtils.isLeoFloodgateFeatureInTest(
			'ccs.ui.leo.experience',
			SearchHostContext.getContext(),
		);
		if (!isLeoEnabledFromFloodgate) {
			dispatch({
				type: ActionTypes.SET_QUERY_INTENT,
				payload: 'intent_not_found',
			});
			return;
		}
		const {
			environment,
			imsInfo: { accessToken },
			hostInfo: { apiKey },
		} = SearchHostContext.getContext();
		const { LeoApiClient }: any = await import(/* webpackChunkName: "leo" */ '@leo/leo-ui');
		const queryIntentResponse = await LeoApiClient.getQueryIntent({
			query,
			accessToken,
			environment,
			xApiKey: apiKey,
		});

		const queryIntentResponseSorted = queryIntentResponse.intents.sort((a, b) => b.score - a.score);
		const selectedIntent = queryIntentResponseSorted[0].intent || 'intent_not_found';
		dispatch({
			type: ActionTypes.SET_QUERY_INTENT,
			payload: selectedIntent,
		});
	} catch (e) {
		getLogger().error('actions: fetchQueryIntent', 'Failed to get query intent ', e);
		dispatch({
			type: ActionTypes.SET_QUERY_INTENT,
			payload: 'intent_not_found',
		});
	}
};

export const getLeoSession = () => async (dispatch: (...args: Array<any>) => any) => {
	const {
		environment,
		imsInfo: { accessToken },
		hostInfo: { apiKey },
	} = SearchHostContext.getContext();
	const authenticated = SearchHostContext.isAuthenticated();
	if (authenticated) {
		const { LeoApiClient }: any = await import(/* webpackChunkName: "leo" */ '@leo/leo-ui');
		LeoApiClient.init({
			accessToken,
			xApiKey: apiKey,
			environment,
			surface: {
				skipAnalytics: SearchHostContext.getOptOutAnalytics(),
			},
		});
	}
};
